import useConceptsLookup from "catalog/hooks/useConceptsLookup";
import ConceptEntity from "catalog/types/enums/conceptEntity";
import { ConceptSearchResponse, EntityInfo } from "catalog/types/interfaces/conceptResponse";
import queryString from "query-string";
import { ArrayParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import { useDebounce } from "~/hooks/useDebounce";
import { useFetcher } from "~/hooks/useFetcher";

const { VITE_CONCEPT_API } = import.meta.env;

const indexMappings: Partial<Record<ConceptEntity, string>> = {
  [ConceptEntity.SITE]: "cancers-concept-search-index",
  [ConceptEntity.QUESTION_GROUP]: "qgroups-concept-search-index",
  [ConceptEntity.DERIVED_GROUP]: "dgroups-concept-search-index",
};

const useConceptSearch = () => {
  const { data: lookup } = useConceptsLookup();
  const [params] = useQueryParams({
    q: withDefault(StringParam, ""),
    filters: withDefault(ArrayParam, []),
  });
  const debouncedQuery = useDebounce(params.q);

  const qs = queryString.stringify({
    q: debouncedQuery,
    indices: params.filters.length
      ? params.filters.map((filter) => indexMappings[filter as ConceptEntity])
      : Object.values(indexMappings),
    size: 50,
  });

  const { data, ...rest } = useFetcher<ConceptSearchResponse>(
    debouncedQuery ? `${VITE_CONCEPT_API}/search?${qs}` : null
  );

  return {
    data: data?.hits?.hits
      // Only return results that have a matching entry in lookup table
      ?.filter(({ _id }) => lookup?.data?.[_id])
      .map(({ _id, _source }) => ({
        id: _id,
        category: _source.concept_category,
        info: lookup!.data?.[_id] as EntityInfo,
      })),
    ...rest,
  };
};

export const useConceptTotal = () => {
  const { data: results } = useConceptSearch();
  const { data: lookup } = useConceptsLookup();

  // Filter out leaf node concepts from overall list in lookup response
  const conceptDetails = Object.values(lookup?.data ?? {}).filter(({ type }) =>
    [ConceptEntity.SITE, ConceptEntity.QUESTION_GROUP].includes(type)
  );

  // If there is a search response, use the total of subset results (capped to 50)
  if (results) {
    return results.length;
  }

  return conceptDetails.length;
};

export default useConceptSearch;
